/**
 * Quem está usando o app, e se a porta está aberta.
 *
 * A sessão de verdade é o cookie `ns_sess`, que é `HttpOnly`: o JavaScript não
 * o lê nem o apaga. Então aqui não se guarda credencial nenhuma, só duas
 * coisas que o cookie não conta: o nome de quem entrou (para a tela cumprimentar
 * a pessoa certa) e se este aparelho foi trancado à mão.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { ErroAPI } from '../api/client';
import type { AuthStatus } from '../types/auth';
import type { Casa } from '../types/casa';
import { ligacao, quandoExpirar } from './ligacao';

const CHAVE_QUEM = 'ns_quem';
const CHAVE_TRANCA = 'ns_trancado';

export type EstadoSessao = 'verificando' | 'fora' | 'dentro';

interface Sessao {
  estado: EstadoSessao;
  /** Slug de quem entrou. Vem do login; o servidor não tem rota que devolva. */
  quem: string | null;
  casa: Casa | null;
  status: AuthStatus | null;
  entrar: (passcode: string) => Promise<void>;
  /**
   * Entra com uma chave de acesso. A credencial é o que o aparelho devolveu da
   * cerimônia, e é ela que diz quem é: aqui não se passa slug nenhum.
   */
  entrarComChave: (credencial: unknown) => Promise<void>;
  /**
   * Primeiro acesso: escolhe a senha de um lugar que ainda não tem dono. O
   * servidor já devolve a sessão junto, então quem cadastra entra na mesma
   * viagem — não há um segundo login logo em seguida.
   */
  cadastrar: (slug: string, passcode: string, nome?: string) => Promise<void>;
  /**
   * Tranca este aparelho. Não encerra a sessão no servidor, porque não existe
   * rota para isso e o cookie é `HttpOnly`; volta a pedir a senha, e é só.
   */
  trancar: () => void;
  recarregarCasa: () => Promise<void>;
  /**
   * Pergunta de novo quem já tem senha nesta casa. Ao contrário do `status` da
   * abertura, este **deixa o erro subir**: quem chama é a porta, que precisa
   * saber a diferença entre "ninguém falta cadastrar" e "o servidor não
   * respondeu" para mostrar uma coisa ou a outra.
   */
  recarregarPorta: () => Promise<AuthStatus>;
}

const Contexto = createContext<Sessao | null>(null);

export function ProvedorDeSessao({ children }: { children: ReactNode }) {
  const { api, armazem, guardado, aoVivo } = ligacao();
  const [estado, setEstado] = useState<EstadoSessao>('verificando');
  const [quem, setQuem] = useState<string | null>(() => guardado.ler(CHAVE_QUEM));
  const [casa, setCasa] = useState<Casa | null>(null);
  const [status, setStatus] = useState<AuthStatus | null>(null);

  const sairPorExpirar = useCallback(() => {
    armazem.desligar();
    setEstado('fora');
  }, [armazem]);

  useEffect(() => quandoExpirar(sairPorExpirar), [sairPorExpirar]);

  const recarregarCasa = useCallback(async () => {
    setCasa(await api.casa());
  }, [api]);

  // Onde há fluxo, `armazem.ligar()` é quem enche as coleções. Onde não há, o
  // `usarColecao` busca a sua; ligar aqui só gastaria um erro de EventSource.
  const abrirOArmazem = useCallback(() => {
    if (aoVivo) armazem.ligar();
  }, [armazem, aoVivo]);

  // Na abertura: o `status` responde sem sessão e traz os nomes, que a porta
  // precisa antes de qualquer login. O `GET /casa` diz se o cookie ainda vale,
  // que é a única forma de saber sem uma rota de "quem sou eu".
  //
  // Os dois saem juntos, e não um depois do outro: nenhum precisa da resposta
  // do outro, e em fila cada um cobrava os 15 s de prazo do cliente por sua
  // conta -- meio minuto de "Abrindo..." numa rede ruim, que de fora não se
  // distingue de um app que não abre. Juntos, o pior caso é o de um só.
  //
  // A tranca é lida antes de sair pedindo: aparelho trancado não tem o que
  // fazer com o `GET /casa`, e disparar o pedido só para descartá-lo mandaria
  // o cookie de casa para quem pediu para esquecer dele neste aparelho.
  useEffect(() => {
    let vivo = true;
    const relogios: ReturnType<typeof setTimeout>[] = [];
    const espera = (ms: number) =>
      new Promise<void>((pronto) => {
        relogios.push(setTimeout(pronto, ms));
      });
    const trancado = guardado.ler(CHAVE_TRANCA) === '1';
    const oStatus = api.status().catch(() => null);
    const aCasa = trancado ? null : api.casa().catch(() => null);

    // Um tropeço de rede na abertura não pode virar app que não abre. O pedido
    // da abertura é o mais caro do app -- sai enquanto o aparelho ainda está
    // lendo o pacote -- e quando ele estourava o prazo a porta ficava presa no
    // "sem servidor" até alguém tocar em "Tentar de novo", porque nada mais
    // pergunta pelo `status`. Isto insiste por trás da porta já desenhada: se
    // o servidor responder, ela troca de recado sozinha. Duas tentativas e
    // para -- o que não voltou nesse tempo é caso de tentar à mão.
    const insistir = async () => {
      for (const pausa of [1500, 4000]) {
        await espera(pausa);
        if (!vivo) return;
        const s = await api.status().catch(() => null);
        if (!vivo || !s) continue;
        setStatus(s);
        // A casa do `GET /casa` continua valendo mais que a da calçada: se ela
        // já chegou, esta não a atropela.
        setCasa((antes) => antes ?? s.casa);
        return;
      }
    };

    void (async () => {
      const s = await oStatus;
      if (!vivo) return;
      if (s) {
        setStatus(s);
        setCasa(s.casa);
      } else {
        // Sem servidor a porta ainda abre, com o recado de sempre; o erro de
        // entrar continua aparecendo na hora de entrar.
        void insistir();
      }

      if (trancado) {
        setEstado('fora');
        return;
      }

      const c = await aCasa;
      if (!vivo) return;
      // A casa do `status` é a que se vê da calçada; a do `GET /casa` é a de
      // dentro. Quando as duas voltam, quem vale é esta -- por isso ela é
      // esperada depois, e não a primeira que chegar.
      if (c) {
        setCasa(c);
        abrirOArmazem();
        setEstado('dentro');
      } else {
        setEstado('fora');
      }
    })();

    return () => {
      vivo = false;
      for (const r of relogios) clearTimeout(r);
    };
  }, [api, guardado, abrirOArmazem]);

  // O que acontece depois de o servidor aceitar a senha, seja ela recém-criada
  // ou de sempre: as duas portas terminam igual, e escrever isso duas vezes é
  // como uma delas acaba esquecendo de destrancar o aparelho.
  const depoisDeEntrar = useCallback(
    async (slug: string) => {
      guardado.gravar(CHAVE_QUEM, slug);
      guardado.apagar(CHAVE_TRANCA);
      setQuem(slug);
      try {
        setCasa(await api.casa());
      } catch (err) {
        // Entrou mas a casa não veio: seguir mesmo assim é melhor que barrar na
        // porta com a senha certa. A tela recarrega a casa depois.
        if (!(err instanceof ErroAPI)) throw err;
      }
      abrirOArmazem();
      setEstado('dentro');
    },
    [api, guardado, abrirOArmazem],
  );

  const entrar = useCallback(
    async (passcode: string) => {
      const r = await api.entrar(passcode);
      await depoisDeEntrar(r.slug);
    },
    [api, depoisDeEntrar],
  );

  const entrarComChave = useCallback(
    async (credencial: unknown) => {
      const r = await api.entrarComChave(credencial);
      await depoisDeEntrar(r.slug);
    },
    [api, depoisDeEntrar],
  );

  const cadastrar = useCallback(
    async (slug: string, passcode: string, nome?: string) => {
      const r = await api.cadastrar(slug, passcode, nome);
      await depoisDeEntrar(r.slug);
    },
    [api, depoisDeEntrar],
  );

  const recarregarPorta = useCallback(async () => {
    const s = await api.status();
    setStatus(s);
    setCasa(s.casa);
    return s;
  }, [api]);

  const trancar = useCallback(() => {
    guardado.gravar(CHAVE_TRANCA, '1');
    armazem.desligar();
    setEstado('fora');
  }, [armazem, guardado]);

  const valor = useMemo<Sessao>(
    () => ({
      estado,
      quem,
      casa,
      status,
      entrar,
      entrarComChave,
      cadastrar,
      trancar,
      recarregarCasa,
      recarregarPorta,
    }),
    [
      estado,
      quem,
      casa,
      status,
      entrar,
      entrarComChave,
      cadastrar,
      trancar,
      recarregarCasa,
      recarregarPorta,
    ],
  );

  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}

export function usarSessao(): Sessao {
  const s = useContext(Contexto);
  if (!s) throw new Error('usarSessao fora do ProvedorDeSessao.');
  return s;
}

/** A pessoa de quem está usando, tirada da casa. */
export function usarPessoa() {
  const { casa, quem } = usarSessao();
  return useMemo(
    () => casa?.pessoas.find((p) => p.slug === quem) ?? null,
    [casa, quem],
  );
}
